/**
 * useGymMemberships Hook
 *
 * Custom hook for querying the current user's gym memberships from Firestore.
 * Provides information about which gyms a user follows, their roles, and home gym.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { GymMembership, GymRole } from '@flash/shared';

/**
 * Convert Firestore document to GymMembership type
 */
const docToGymMembership = (docData: DocumentData): GymMembership => {
  return {
    id: docData['id'] as string,
    odKey: docData['odKey'] as string,
    userId: docData['userId'] as string,
    gymId: docData['gymId'] as string,
    role: docData['role'] as GymRole,
    isHomeGym: docData['isHomeGym'] as boolean,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * State interface for gym memberships query
 */
interface UseGymMembershipsState {
  /** List of user's gym memberships */
  memberships: GymMembership[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGymMemberships hook
 */
interface UseGymMembershipsResult extends UseGymMembershipsState {
  /** Refetch memberships from Firestore */
  refetch: () => Promise<void>;
  /** List of gym IDs the user follows */
  followedGymIds: string[];
  /** Check if user follows a specific gym */
  isFollowing: (gymId: string) => boolean;
  /** Get user's role at a specific gym */
  getRoleAtGym: (gymId: string) => GymRole | null;
  /** Get membership for a specific gym */
  getMembership: (gymId: string) => GymMembership | null;
}

/**
 * Hook to fetch and subscribe to the current user's gym memberships
 *
 * @param options - Optional configuration
 * @param options.realtime - Whether to use real-time listener (default: true)
 * @returns Object containing memberships, loading state, error, and utility functions
 *
 * @example
 * ```tsx
 * const { memberships, isFollowing, loading } = useGymMemberships();
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <div>
 *     <p>Following {memberships.length} gyms</p>
 *     {isFollowing('gym-123') && <p>Already following!</p>}
 *   </div>
 * );
 * ```
 */
export const useGymMemberships = (
  options: { realtime?: boolean } = {}
): UseGymMembershipsResult => {
  const { realtime = true } = options;
  const { user } = useAuth();

  const [state, setState] = useState<UseGymMembershipsState>({
    memberships: [],
    loading: true,
    error: null,
  });

  /**
   * Fetch memberships from Firestore (one-time)
   */
  const fetchMemberships = useCallback(async (): Promise<void> => {
    if (!user) {
      setState({ memberships: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const membershipsRef = collection(db, 'gymMemberships');
      const membershipsQuery = query(membershipsRef, where('userId', '==', user.uid));

      const snapshot = await getDocs(membershipsQuery);

      const memberships: GymMembership[] = snapshot.docs.map((docSnapshot) =>
        docToGymMembership(docSnapshot.data())
      );

      setState({ memberships, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch memberships';
      console.error('Error fetching gym memberships:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [user]);

  // Real-time subscription effect
  useEffect(() => {
    if (!user) {
      setState({ memberships: [], loading: false, error: null });
      return;
    }

    // For non-realtime mode, just fetch once
    if (!realtime) {
      fetchMemberships();
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const membershipsRef = collection(db, 'gymMemberships');
    const membershipsQuery = query(membershipsRef, where('userId', '==', user.uid));

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      membershipsQuery,
      (snapshot) => {
        const memberships: GymMembership[] = snapshot.docs.map((docSnapshot) =>
          docToGymMembership(docSnapshot.data())
        );

        setState({ memberships, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch memberships';
        console.error('Error in gym memberships subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, realtime, fetchMemberships]);

  /**
   * Get list of gym IDs the user follows
   */
  const followedGymIds = useMemo(() => {
    return state.memberships.map((m) => m.gymId);
  }, [state.memberships]);

  /**
   * Check if user follows a specific gym
   */
  const isFollowing = useCallback(
    (gymId: string): boolean => {
      return state.memberships.some((m) => m.gymId === gymId);
    },
    [state.memberships]
  );

  /**
   * Get user's role at a specific gym
   */
  const getRoleAtGym = useCallback(
    (gymId: string): GymRole | null => {
      const membership = state.memberships.find((m) => m.gymId === gymId);
      return membership?.role ?? null;
    },
    [state.memberships]
  );

  /**
   * Get membership for a specific gym
   */
  const getMembership = useCallback(
    (gymId: string): GymMembership | null => {
      return state.memberships.find((m) => m.gymId === gymId) ?? null;
    },
    [state.memberships]
  );

  return {
    ...state,
    refetch: fetchMemberships,
    followedGymIds,
    isFollowing,
    getRoleAtGym,
    getMembership,
  };
};
